import { create } from 'zustand';

import { api } from '../../lib/api.js';
import { eventSocket } from '../../lib/ws.js';
import { loadPrism } from '../../engine/prism.js';
import type { SoundDoc } from '../../lib/sound-doc.js';
import { renderSound } from '../../lib/sound-render.js';
import { useWorkspace } from '../../state/workspace.js';
import { isNum, isObj } from './param-values.js';

/**
 * Backend and engine access for the Sound tab editors: the shared download
 * cache (`GET /api/sound/cache`, `POST /api/sound/cache/download`), presets
 * (`GET /api/sound/presets`) and preview rendering/playback through the wasm
 * `SoundRenderer` (engine/docs/systems/sound.md). Responses are normalised
 * defensively because the backend is built concurrently.
 */

/* ------------------------------------------------------------------ */
/* Download cache                                                      */

export type CacheKind = 'soundfont' | 'voice';

export interface CacheEntry {
  kind: CacheKind;
  id: string;
  name: string;
  installed: boolean;
  /** Bytes (0 = unknown). */
  size: number;
  license?: string;
  attribution?: string;
  url?: string;
  language?: string;
  quality?: string;
  speakers?: number;
  description?: string;
  isDefault?: boolean;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);

function normEntry(raw: unknown, kindHint: CacheKind | undefined, installedHint: boolean | undefined): CacheEntry | null {
  if (typeof raw === 'string') raw = { id: raw };
  if (!isObj(raw)) return null;
  const id = str(raw.id) ?? str(raw.key) ?? str(raw.name);
  if (!id) return null;
  const k = str(raw.kind) ?? str(raw.type);
  const kind: CacheKind | undefined = k === 'voice' || k === 'soundfont' ? k : kindHint ?? (/\.sf2$/i.test(id) || /soundfont|gs$/i.test(id) ? 'soundfont' : 'voice');
  const size = [raw.size, raw.bytes, raw.sizeBytes].find(isNum) ?? 0;
  const lang = isObj(raw.language) ? str(raw.language.code) ?? str(raw.language.name_english) : str(raw.language) ?? str(raw.lang);
  const speakers = isNum(raw.speakers) ? raw.speakers : isNum(raw.num_speakers) ? raw.num_speakers : undefined;
  return {
    kind,
    id,
    name: str(raw.name) ?? str(raw.title) ?? id,
    installed: typeof raw.installed === 'boolean' ? raw.installed : installedHint ?? false,
    size,
    license: str(raw.license),
    attribution: str(raw.attribution),
    url: str(raw.url),
    language: lang ?? (kind === 'voice' ? id.split('-')[0] : undefined),
    quality: str(raw.quality) ?? (kind === 'voice' ? id.split('-')[2] : undefined),
    speakers,
    description: str(raw.description),
    isDefault: raw.default === true || raw.isDefault === true,
  };
}

/**
 * Normalises `GET /api/sound/cache`: accepts `{ installed: [...], available |
 * catalogue | catalog: [...] }`, `{ soundfonts: [...], voices: [...] }` (each
 * either arrays or `{ installed, available }`), or a flat array. Installed
 * entries win over catalogue duplicates.
 */
export function normalizeCache(json: unknown): CacheEntry[] {
  const out = new Map<string, CacheEntry>();
  const add = (list: unknown, kind?: CacheKind, installed?: boolean) => {
    if (!Array.isArray(list)) {
      if (isObj(list)) {
        add(list.installed, kind, true);
        for (const k of ['available', 'catalogue', 'catalog', 'items', 'entries']) add(list[k], kind, false);
      }
      return;
    }
    for (const raw of list) {
      const e = normEntry(raw, kind, installed);
      if (!e) continue;
      const key = `${e.kind}:${e.id}`;
      const prev = out.get(key);
      if (!prev) out.set(key, e);
      else {
        const merged: CacheEntry = { ...e };
        for (const [k, v] of Object.entries(prev)) if (v !== undefined) (merged as unknown as Record<string, unknown>)[k] = v;
        merged.installed = prev.installed || e.installed;
        merged.size = prev.size || e.size;
        out.set(key, merged);
      }
    }
  };
  if (Array.isArray(json)) add(json);
  else if (isObj(json)) {
    add(json.installed, undefined, true);
    for (const k of ['available', 'catalogue', 'catalog', 'entries', 'items']) add(json[k], undefined, false);
    add(json.soundfonts, 'soundfont');
    add(json.voices, 'voice');
  }
  return [...out.values()];
}

export function formatBytes(n: number): string {
  if (!n) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u++;
  }
  return `${v >= 100 || u === 0 ? Math.round(v) : v.toFixed(1)} ${units[u]}`;
}

export interface DownloadState {
  /** 0..1, or null when the size is unknown. */
  progress: number | null;
  error?: string;
}

interface CacheStore {
  entries: CacheEntry[];
  loading: boolean;
  error: string | null;
  loaded: boolean;
  downloads: Record<string, DownloadState>;
  load: (force?: boolean) => Promise<void>;
  download: (kind: CacheKind, id: string) => Promise<boolean>;
}

const dlKey = (kind: string, id: string) => `${kind}:${id}`;

/** Download progress events from the backend's event socket (loosely typed). */
function progressOf(ev: Record<string, unknown>): { key: string; progress: number | null; done: boolean; error?: string } | null {
  const type = str(ev.type) ?? '';
  if (!/sound/i.test(type) || !/download|cache/i.test(type)) return null;
  const payload = isObj(ev.payload) ? { ...ev, ...ev.payload } : ev;
  const id = str(payload.id);
  if (!id) return null;
  const kind = str(payload.kind) ?? 'voice';
  const received = [payload.received, payload.loaded, payload.bytes, payload.downloaded].find(isNum);
  const total = [payload.total, payload.size, payload.totalBytes].find(isNum);
  const p = isNum(payload.progress) ? (payload.progress > 1 ? payload.progress / 100 : payload.progress) : received !== undefined && total ? received / total : null;
  const done = payload.done === true || payload.status === 'done' || payload.state === 'done' || /done|complete|finish/i.test(type);
  const error = str(payload.error);
  return { key: dlKey(kind, id), progress: p, done, error };
}

export const useSoundCache = create<CacheStore>((set, get) => ({
  entries: [],
  loading: false,
  error: null,
  loaded: false,
  downloads: {},
  load: async (force = false) => {
    if (get().loading || (get().loaded && !force)) return;
    set({ loading: true });
    try {
      const json = await api.get<unknown>('/sound/cache');
      set({ entries: normalizeCache(json), loading: false, loaded: true, error: null });
    } catch (e) {
      set({ loading: false, loaded: true, error: e instanceof Error ? e.message : 'Could not read the sound cache.' });
    }
  },
  download: async (kind, id) => {
    const key = dlKey(kind, id);
    set((s) => ({ downloads: { ...s.downloads, [key]: { progress: null } } }));
    const off = eventSocket.subscribe((event) => {
      const p = progressOf(event as unknown as Record<string, unknown>);
      if (!p || p.key !== key || p.done) return;
      set((s) => ({ downloads: { ...s.downloads, [key]: { progress: p.progress, error: p.error } } }));
    });
    try {
      await api.post('/sound/cache/download', { kind, id });
      set((s) => {
        const downloads = { ...s.downloads };
        delete downloads[key];
        return { downloads };
      });
      await get().load(true);
      return true;
    } catch (e) {
      const error = e instanceof Error ? e.message : 'Download failed.';
      set((s) => ({ downloads: { ...s.downloads, [key]: { progress: null, error } } }));
      return false;
    } finally {
      off();
    }
  },
}));

/* ------------------------------------------------------------------ */
/* Presets                                                             */

export interface SoundPreset {
  id: string;
  name: string;
  category: string;
  description?: string;
  tags: string[];
  /** The preset document, when the listing includes it. */
  doc?: unknown;
}

function normPreset(raw: unknown, category?: string): SoundPreset | null {
  if (!isObj(raw)) return null;
  const doc = raw.doc ?? raw.document ?? raw.json ?? raw.sound ?? (raw.format === 'prism.sound/1' ? raw : undefined);
  const docName = isObj(doc) ? str(doc.name) : undefined;
  const id = str(raw.id) ?? str(raw.path) ?? str(raw.name) ?? docName;
  if (!id) return null;
  return {
    id,
    name: str(raw.name) ?? docName ?? id,
    category: str(raw.category) ?? category ?? (isObj(doc) ? str(doc.kind) : undefined) ?? 'Other',
    description: str(raw.description) ?? (isObj(doc) ? str(doc.description) : undefined),
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [],
    doc: typeof doc === 'string' ? safeParse(doc) : doc,
  };
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/** Normalises `GET /api/sound/presets` (array, `{ presets }` or `{ categories: { name: [...] } }`). */
export function normalizePresets(json: unknown): SoundPreset[] {
  const out: SoundPreset[] = [];
  const add = (list: unknown, category?: string) => {
    if (Array.isArray(list)) for (const r of list) {
      const p = normPreset(r, category);
      if (p) out.push(p);
    }
  };
  if (Array.isArray(json)) add(json);
  else if (isObj(json)) {
    add(json.presets);
    const cats = json.categories ?? json.byCategory;
    if (isObj(cats)) for (const [name, list] of Object.entries(cats)) add(list, name);
    else if (Array.isArray(cats)) for (const c of cats) if (isObj(c)) add(c.presets ?? c.items, str(c.name) ?? str(c.id));
  }
  return out;
}

/** Loads a preset's document (from the listing, or `GET /api/sound/presets/:id`). */
export async function presetDoc(p: SoundPreset): Promise<unknown> {
  if (p.doc !== undefined) return p.doc;
  const json = await api.get<unknown>(`/sound/presets/${encodeURIComponent(p.id)}`);
  const n = normPreset(isObj(json) && isObj(json.preset) ? json.preset : json);
  return n?.doc ?? json;
}

/* ------------------------------------------------------------------ */
/* Preview rendering                                                   */

export interface RenderedSound {
  left: Float32Array;
  right: Float32Array;
  rate: number;
  warnings: string[];
  analysis: unknown;
}

/** The wasm `SoundRenderer` (prism_wasm), typed locally until prism.ts declares it. */
interface SoundRendererApi {
  addFile(path: string, bytes: Uint8Array): void;
  render(json: string, path: string, variant?: string): RenderedSound;
  spectrogram?(left: Float32Array, right: Float32Array, rate: number, width: number, height: number): Uint8Array;
  free?(): void;
}

export type PreviewRenderer = (doc: unknown, path: string) => Promise<RenderedSound>;

let custom: PreviewRenderer | null = null;
let shared: SoundRendererApi | null = null;

/**
 * Lets the Sound tab plug in its own renderer (one that has the project's
 * samples and cached SoundFont/voices loaded). Pass null to restore the
 * default bare wasm renderer.
 */
export function setPreviewRenderer(fn: PreviewRenderer | null): void {
  custom = fn;
}

async function wasmRenderer(): Promise<SoundRendererApi> {
  if (shared) return shared;
  const mod = (await loadPrism()) as unknown as { SoundRenderer?: new () => SoundRendererApi } | null;
  if (!mod?.SoundRenderer) throw new Error('The sound renderer is not in this engine build — run pnpm engine:build.');
  shared = new mod.SoundRenderer();
  return shared;
}

/** Renders a (possibly unsaved) sound document for preview. */
export async function renderPreview(doc: unknown, path = 'sounds/_preview.sound.json'): Promise<RenderedSound> {
  if (custom) return custom(doc, path);
  const parsed: unknown = typeof doc === 'string' ? JSON.parse(doc) : doc;
  const projectId = useWorkspace.getState().projectId;
  if (projectId !== null) {
    // The Sound tab's renderer: loads referenced project files and cached SoundFont/voices.
    const out = await renderSound({ projectId, path, doc: parsed as SoundDoc });
    return { left: out.audio.left, right: out.audio.right, rate: out.audio.rate, warnings: out.warnings, analysis: out.analysis };
  }
  const r = await wasmRenderer();
  return r.render(JSON.stringify(parsed), path);
}

/** PNG spectrogram via the wasm renderer, or null when unavailable. */
export async function wasmSpectrogram(s: RenderedSound, width: number, height: number): Promise<Uint8Array | null> {
  try {
    const r = await wasmRenderer();
    return r.spectrogram ? r.spectrogram(s.left, s.right, s.rate, width, height) : null;
  } catch {
    return null;
  }
}

let ctx: AudioContext | null = null;
let current: AudioBufferSourceNode | null = null;

/** Stops whatever preview is playing. */
export function stopPreview(): void {
  try {
    current?.stop();
  } catch {
    /* already stopped */
  }
  current = null;
}

/** Plays a rendered buffer (stopping any previous preview); resolves when it ends. */
export function playRendered(s: RenderedSound, offset = 0): Promise<void> {
  stopPreview();
  ctx ??= new AudioContext({ sampleRate: s.rate });
  const buf = ctx.createBuffer(2, Math.max(1, s.left.length), s.rate);
  buf.copyToChannel(s.left as Float32Array<ArrayBuffer>, 0);
  buf.copyToChannel((s.right.length ? s.right : s.left) as Float32Array<ArrayBuffer>, 1);
  const node = ctx.createBufferSource();
  node.buffer = buf;
  node.connect(ctx.destination);
  void ctx.resume();
  node.start(0, Math.max(0, offset));
  current = node;
  return new Promise((resolve) => {
    node.onended = () => {
      if (current === node) current = null;
      resolve();
    };
  });
}

/** Render + play; returns the render (for analysis) or throws with the reason. */
export async function previewDoc(doc: unknown, path?: string): Promise<RenderedSound> {
  const r = await renderPreview(doc, path);
  void playRendered(r);
  return r;
}

/** A minimal `prism.sound/1` document around a fragment (layer, voice, instrument) for previews. */
export function previewDocument(name: string, body: Record<string, unknown>, from?: unknown): Record<string, unknown> {
  const base: Record<string, unknown> = { format: 'prism.sound/1', name, seed: 1 };
  if (isObj(from)) {
    for (const k of ['variables', 'instruments', 'tempo', 'timeSignature', 'key', 'scale', 'seed']) if (from[k] !== undefined) base[k] = from[k];
    if (isObj(from.master) && from.master.returns !== undefined) base.master = { returns: from.master.returns };
  }
  return { ...base, ...body };
}
